import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  createCreditCardToken,
  deleteCreditCardToken,
  fetchCreditCardTokens,
  setDefaultCreditCardToken,
} from "../../processes/creditCardTokenProcesses";
import { fetchInvoices } from "../../processes/invoicesProcesses";
import {
  cancelSubscription,
  fetchSubscriptions,
  retrySubscriptionCharge,
  updateSubscriptionPaymentMethod,
} from "../../processes/subscriptionProcesses";
import { getAllCreditCardTokens } from "../../selectors/creditCardTokenSelectors";
import {
  getAllSubscriptions,
  getSubscriptionAccessEndDate,
  getSubscriptionCanRefundUntil,
  getSubscriptionError,
  getSubscriptionLoading,
} from "../../selectors/subscriptionSelectors";
import { getCurrentUser } from "../../selectors/userSelectors";
import FinanceSubscriptionsPanel from "./FinanceSubscriptionsPanel";

function FinanceSubscriptionsContainer(props) {
  const { dispatch, currentUser } = props;
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [paymentMethodActionId, setPaymentMethodActionId] = useState(null);
  const [subscriptionActionId, setSubscriptionActionId] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptions(dispatch);
    fetchInvoices(dispatch);
    fetchCreditCardTokens(dispatch);
  }, [dispatch]);

  const handleUpdatePaymentMethod = useCallback((subscriptionId, cardData) => {
    setPaymentMethodLoading(true);

    return updateSubscriptionPaymentMethod(dispatch, subscriptionId, cardData)
      .then((response) => {
        fetchCreditCardTokens(dispatch);
        return response;
      })
      .finally(() => setPaymentMethodLoading(false));
  }, [dispatch]);

  const handleSetDefaultPaymentMethod = useCallback((tokenId) => {
    setPaymentMethodActionId(`default-${tokenId}`);

    return setDefaultCreditCardToken(dispatch, tokenId)
      .finally(() => setPaymentMethodActionId(null));
  }, [dispatch]);

  const handleDeletePaymentMethod = useCallback((tokenId) => {
    setPaymentMethodActionId(`delete-${tokenId}`);

    return deleteCreditCardToken(dispatch, tokenId)
      .finally(() => setPaymentMethodActionId(null));
  }, [dispatch]);

  const handleCancelSubscription = useCallback((subscriptionId, refundLastPayment = false) => {
    setSubscriptionActionId(`cancel-${subscriptionId}`);

    return cancelSubscription(dispatch, subscriptionId, refundLastPayment)
      .finally(() => setSubscriptionActionId(null));
  }, [dispatch]);

  const handleRetryCharge = useCallback((subscriptionId, cardId) => {
    setRetryLoading(true);
    return retrySubscriptionCharge(dispatch, subscriptionId, cardId)
      .finally(() => setRetryLoading(false));
  }, [dispatch]);

  const handleAddCard = useCallback((cardData) => (
    createCreditCardToken(dispatch, cardData)
      .then(() => fetchCreditCardTokens(dispatch))
  ), [dispatch]);

  return (
    <FinanceSubscriptionsPanel
      {...props}
      paymentMethodLoading={paymentMethodLoading}
      paymentMethodActionId={paymentMethodActionId}
      subscriptionActionId={subscriptionActionId}
      onUpdatePaymentMethod={handleUpdatePaymentMethod}
      onSetDefaultPaymentMethod={handleSetDefaultPaymentMethod}
      onDeletePaymentMethod={handleDeletePaymentMethod}
      onCancelSubscription={handleCancelSubscription}
      onRetryCharge={handleRetryCharge}
      retryLoading={retryLoading}
      onAddCard={handleAddCard}
      requiresCpf={!currentUser?.document}
    />
  );
}

function mapStateToProps(state) {
  const subscriptions = getAllSubscriptions(state);

  return {
    subscriptions,
    subscriptionCancelOptions: subscriptions.reduce((options, subscription) => ({
      ...options,
      [subscription.id]: {
        accessEndDate: getSubscriptionAccessEndDate(state, subscription.id),
        canRefundUntil: getSubscriptionCanRefundUntil(state, subscription.id),
      },
    }), {}),
    creditCardTokens: getAllCreditCardTokens(state),
    currentUser: getCurrentUser(state),
    invoices: state.invoicesList.invoices || [],
    loading: getSubscriptionLoading(state),
    error: getSubscriptionError(state),
  };
}

export default connect(mapStateToProps)(FinanceSubscriptionsContainer);
